/**
 * VirtualStagingModal — landlord picks a listing photo URL, we render
 * 3 furnished variants via /api/staging/render. Landlord can copy the
 * URL of any variant to use on their listing.
 */
import { useState } from "react";
import { Modal, Button, Input, Pill } from "@/components/ui/Primitives";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

export default function VirtualStagingModal({ open, onClose, defaultSourceUrl = "" }) {
  const [sourceUrl, setSourceUrl] = useState(defaultSourceUrl);
  const [context, setContext] = useState("living-room interior");
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const [copied, setCopied] = useState("");

  async function render() {
    if (!sourceUrl.trim()) return toast.error("Paste a photo URL first.");
    setBusy(true);
    setVariants([]);
    try {
      const r = await fetch(`${API}/api/staging/render`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ source_url: sourceUrl.trim(), context: context.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Staging failed");
      setVariants(d.variants || []);
      toast.success(`Generated ${d.variants?.length || 0} staged variants`);
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  function copyUrl(url) {
    const full = url.startsWith("http") ? url : `${API}${url}`;
    navigator.clipboard.writeText(full).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(""), 1500);
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Virtual staging" testid="staging-modal">
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <Sparkles className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
          <div className="text-xs text-emerald-800">
            We'll render <b>3 furnished layouts</b> over your empty-room photo. Perfect companion to your 3D tour on the listing page.
          </div>
        </div>
        <Input
          label="Photo URL"
          required
          placeholder="https://example.com/empty-living-room.jpg"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          testid="staging-source-url"
        />
        <Input
          label="Room context (optional)"
          placeholder="living-room interior · bedroom · kitchen · patio"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          testid="staging-context"
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={render} disabled={busy} data-testid="staging-render">
            {busy ? <><Loader2 className="w-4 h-4 inline mr-1 animate-spin"/>Rendering (may take ~20s)…</> : <><Sparkles className="w-4 h-4 inline mr-1"/>Generate 3 variants</>}
          </Button>
        </div>

        {variants.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="staging-variants">
            {variants.map((v) => (
              <div key={v.style} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`staging-variant-${v.style}`}>
                <img src={`${API}${v.url}`} alt={v.style} className="w-full aspect-square object-cover"/>
                <div className="p-2 flex items-center justify-between">
                  <Pill tone="slate">{v.style}</Pill>
                  <button onClick={() => copyUrl(v.url)} className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid={`staging-copy-${v.style}`}>
                    {copied === v.url ? <><Check className="w-3 h-3"/>Copied</> : <><Copy className="w-3 h-3"/>Copy URL</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
